// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { useSustainability } from './use-sustainability';
import { ScreenLayout } from '@shared/advisory/account-workflow/layout';
import { NavigationControls } from '@shared/advisory/account-workflow/layout';
import { Translation } from '@shared/ui-kit/providers';
import { ScreenHeader } from '@shared/advisory/account-workflow/layout';
import { type Path } from 'react-hook-form';
import type { SustainabilityFormData } from '@shared/advisory/account-workflow/types';
import { QuestionCard } from '@shared/advisory/account-workflow/components/shared/question-card';
import { QuestionSlider } from '@shared/advisory/account-workflow/components/shared/question-slider';
import { QuestionCheckboxes } from '@shared/advisory/account-workflow/components/shared/question-checkboxes';
import { Card } from '@mui/material';

export const Sustainability = defineComponent({
  displayName: 'Sustainability',
  useSetup: useSustainability,
  View: ({
    form,
    onNext,
    isFormValid,
    sustainabilityCriteria,
    showConditionalSection,
    preferencesQuestion,
    percentageQuestion,
    criteriaQuestion,
    taxonomyQuestion,
    sfdrQuestion,
    piasQuestion,
    isLoading,
  }) => {
    return (
      <ScreenLayout
        actions={
          <NavigationControls
            form="sustainability-form"
            isFormValid={isFormValid}
            loading={isLoading}
          />
        }
      >
        <div className="w-full max-w-6xl">
          <ScreenHeader
            title={
              <Translation i18nKey="sustainability-header.title" ns="components" />
            }
            subtitle={
              <Translation
                i18nKey="sustainability-header.subtitle"
                ns="components"
              />
            }
            className="mb-7"
            data-testid="header"
          />

          <form
            id="sustainability-form"
            onSubmit={form.handleSubmit(onNext)}
            className="flex flex-col gap-4"
          >
            {preferencesQuestion && (
              <QuestionCard
                question={preferencesQuestion}
                control={form.control}
                name={preferencesQuestion.id as Path<SustainabilityFormData>}
                variant="plain"
              />
            )}

            {showConditionalSection && (
              <Card
                className="!flex !flex-col !gap-6 !rounded-lg !p-7"
                variant="outlined"
              >
                {percentageQuestion && (
                  <QuestionSlider
                    question={percentageQuestion}
                    control={form.control}
                    name={percentageQuestion.id as Path<SustainabilityFormData>}
                  />
                )}

                {criteriaQuestion && (
                  <QuestionCheckboxes
                    question={criteriaQuestion}
                    control={form.control}
                    name={criteriaQuestion.id as Path<SustainabilityFormData>}
                  />
                )}

                {sustainabilityCriteria?.includes('eu_taxonomy') &&
                  taxonomyQuestion && (
                    <QuestionCard
                      question={taxonomyQuestion}
                      control={form.control}
                      name={taxonomyQuestion.id as Path<SustainabilityFormData>}
                      variant="plain"
                    />
                  )}

                {sustainabilityCriteria?.includes('sfdr') && sfdrQuestion && (
                  <QuestionCard
                    question={sfdrQuestion}
                    control={form.control}
                    name={sfdrQuestion.id as Path<SustainabilityFormData>}
                    variant="plain"
                  />
                )}

                {sustainabilityCriteria?.includes('pias') && piasQuestion && (
                  <QuestionCard
                    question={piasQuestion}
                    control={form.control}
                    name={piasQuestion.id as Path<SustainabilityFormData>}
                    variant="plain"
                  />
                )}
              </Card>
            )}
          </form>
        </div>
      </ScreenLayout>
    );
  },
});
